<template>
  <link rel="preconnect" href="https://fonts.googleapis.com">
  <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
  <link
    href="https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&family=Roboto:wght@400;500;700&display=swap"
    rel="stylesheet">

  <div ref="rootEl"
    class="min-h-screen font-sans bg-slate-50 dark:bg-[#020617] text-slate-900 dark:text-slate-50 selection:bg-sky-500/30"
    style="font-family: 'Inter', 'Roboto', sans-serif;">
    <HeaderNav />

    <div class="relative w-full pt-32 pb-20 sm:pt-40 sm:pb-24 overflow-hidden">
      <div
        class="absolute top-0 left-1/2 -translate-x-1/2 w-[1000px] h-[500px] bg-sky-400/20 dark:bg-sky-900/20 blur-[100px] rounded-full opacity-50 pointer-events-none">
      </div>

      <div class="relative max-w-5xl mx-auto px-4 sm:px-6 text-center space-y-6">
        <div
          class="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-sky-200/50 bg-sky-50/50 dark:border-sky-800/30 dark:bg-sky-900/10 backdrop-blur-md">
          <span class="flex h-2 w-2 rounded-full bg-sky-500 animate-pulse"></span>
          <span
            class="text-[9px] sm:text-[11px] font-semibold uppercase tracking-[0.2em] text-sky-700 dark:text-sky-300">
            Palette Studio • NexGridCo
          </span>
        </div>

        <h1
          class="text-3xl sm:text-4xl md:text-6xl font-extrabold tracking-tight text-slate-900 dark:text-white leading-[1.1]">
          Start from battle-tested<br />
          <span
            class="text-transparent bg-clip-text bg-gradient-to-r from-sky-500 via-indigo-500 to-sky-500 animate-gradient-x">
            color systems.
          </span>
        </h1>

        <p class="text-sm sm:text-base md:text-lg text-slate-600 dark:text-slate-400 max-w-2xl mx-auto leading-relaxed">
          Explore export templates used inside Palette Studio. Download a PNG preview or jump into the app to customize
          your own colors and gradients.
        </p>
      </div>
    </div>

    <div class="relative w-full mb-10 sm:mb-12">
      <div class="pointer-events-none
           absolute inset-x-0 top-0 bottom-0
           sm:inset-0
           bg-white/80 dark:bg-[#020617]/80
           backdrop-blur-xl
           border-b border-slate-200/50 dark:border-slate-800/50
           shadow-sm">
      </div>

      <div class="relative max-w-7xl mx-auto
           px-3 sm:px-6 lg:px-8
           pt-[env(safe-area-inset-top)]
           pb-3
           sm:pt-0 sm:pb-0
           sm:h-16
           flex flex-wrap sm:flex-nowrap
           items-center gap-2 sm:gap-3">

        <div class="w-full sm:hidden flex items-center justify-between gap-2">
          <a href="https://x.com/nexgridco" target="_blank" class="inline-flex items-center gap-2 h-9 px-3 rounded-full
               border border-slate-200 dark:border-slate-700
               bg-white/80 dark:bg-slate-900/60
               backdrop-blur
               text-xs font-semibold text-slate-700 dark:text-slate-200
               hover:text-sky-500 transition-colors">
            Follow Updates
            <span class="material-symbols-rounded text-[18px]">arrow_outward</span>
          </a>
        </div>

        <div class="w-full sm:flex-1 flex flex-wrap sm:flex-nowrap items-center gap-2 py-1.5 sm:py-2">
          <button v-for="filter in filters" :key="filter.id" @click="selectedFilter = filter.id"
            class="relative group px-4 py-2 rounded-full text-xs font-medium transition-all duration-200 whitespace-nowrap select-none"
            :class="selectedFilter === filter.id
              ? 'bg-slate-900 text-white dark:bg-white dark:text-slate-900 shadow-md ring-1 ring-black/5'
              : 'text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800/50'">
            {{ filter.label }}
          </button>
        </div>


        <div class="hidden sm:flex items-center gap-3 text-xs font-medium text-slate-500 dark:text-slate-400 shrink-0">
          <div class="h-4 w-[1px] bg-slate-300 dark:bg-slate-700"></div>
          <a href="https://x.com/nexgridco" target="_blank"
            class="hover:text-sky-500 transition-colors flex items-center gap-1.5">
            Follow Updates
            <span class="material-symbols-rounded text-[14px]">arrow_outward</span>
          </a>
        </div>
      </div>
    </div>

    <div class="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-32">
      <div class="grid gap-6 sm:gap-8 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-3">
        <article v-for="(tpl) in filteredTemplates" :key="tpl.id"
          class="group relative flex flex-col bg-white dark:bg-[#0B1120] rounded-[24px] border border-slate-200 dark:border-slate-800/80 shadow-[0_2px_10px_rgba(0,0,0,0.03)] hover:shadow-[0_20px_40px_rgba(0,0,0,0.08)] dark:shadow-none dark:hover:shadow-[0_20px_40px_rgba(0,0,0,0.4)] dark:hover:border-slate-700 transition-all duration-300 hover:-translate-y-1 overflow-hidden">
          <div
            class="relative w-full aspect-square bg-slate-100 dark:bg-[#020617] border-b border-slate-100 dark:border-slate-800 overflow-hidden group-hover:opacity-100 transition-opacity">
            <component :is="tpl.component" :colors="tpl.previewColors" />

            <div
              class="absolute inset-0 z-50 bg-slate-900/0 group-hover:bg-slate-900/10 dark:group-hover:bg-white/5 transition-colors duration-300 flex items-center justify-center opacity-0 group-hover:opacity-100 cursor-pointer"
              @click="downloadTemplatePng(tpl)">
              <button type="button"
                class="scale-90 group-hover:scale-100 transition-transform duration-200 shadow-2xl bg-white dark:bg-slate-900 text-slate-900 dark:text-white px-6 py-3 rounded-full font-bold text-sm flex items-center gap-2 pointer-events-none">
                <span class="material-symbols-rounded text-[18px]">download</span>
                Export PNG
              </button>
            </div>
          </div>

          <div class="p-5 flex flex-col flex-1">
            <div class="flex items-start justify-between gap-4 mb-2">
              <div>
                <p class="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-1">{{ tpl.category }}
                </p>
                <h3 class="text-base font-bold text-slate-900 dark:text-slate-100 tracking-tight">{{ tpl.name }}</h3>
                <p class="text-[12px] text-slate-500 dark:text-slate-400 mt-1 line-clamp-2">{{ tpl.description }}</p>
              </div>
            </div>
          </div>
        </article>
      </div>
    </div>

    <section class="flex flex-col items-center gap-4 text-center">
      <p class="text-xs sm:text-[13px] text-slate-500 dark:text-slate-400 max-w-md leading-relaxed">
        Want live palette breakdowns and new template drops? Follow
        <span class="font-semibold text-slate-900 dark:text-slate-50">@nexgridco</span>
        on X.
      </p>
      <div
        class="inline-flex items-center gap-2 rounded-full border border-slate-200/70 bg-white/80 px-3 py-1.5 text-[11px] uppercase tracking-[0.18em] text-slate-600 shadow-sm dark:border-slate-700/80 dark:bg-slate-900/80 dark:text-slate-300">
        Public
      </div>
      <a href="https://x.com/nexgridco" target="_blank" rel="noopener noreferrer"
        class="inline-flex items-center gap-2 rounded-2xl border border-slate-300/60 bg-white px-4 py-2 text-sm font-medium text-slate-700 transition hover:border-slate-900 hover:text-slate-900 dark:border-slate-700/70 dark:bg-slate-900/80 dark:text-slate-300 dark:hover:border-slate-50 dark:hover:text-slate-50">
        <span class="material-symbols-rounded text-[18px]">public</span>
        Follow on X
      </a>
    </section>

    <br />

    <button v-if="showBackToTop" type="button" class="back-to-top" @click="scrollToTop">
      <span class="material-symbols-rounded text-[20px]">arrow_upward</span>
    </button>
    <FooterBar />

    <div class="fixed -left-[9999px] -top-[9999px]" aria-hidden="true">
      <div ref="exportRef" class="relative flex items-center justify-center" :style="{
        width: EXPORT_SIZE + 'px',
        height: EXPORT_SIZE + 'px',
        background: 'transparent'
      }">
        <div
          class="w-full h-full rounded-[40px] bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 overflow-hidden relative">
          <component v-if="currentExportTemplate" :is="currentExportTemplate.component"
            :colors="currentExportTemplate.previewColors" :is-export="true" />
          <div
            class="absolute bottom-10 right-10 text-[14px] text-white/50 tracking-[0.2em] uppercase font-bold mix-blend-overlay z-[60]">
            NexGridCo • Palette Studio
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, ref, defineComponent, h, nextTick, onMounted, onUnmounted } from 'vue'
import html2canvas from 'html2canvas'
import HeaderNav from '../components/marketing/HeaderNav.vue'
import FooterBar from '../components/marketing/FooterBar.vue'

const NAMED_COLORS = [
  { hex: '#000000', name: 'Pure Black' },
  { hex: '#020617', name: 'Midnight' },
  { hex: '#0F172A', name: 'Deep Space' },
  { hex: '#030712', name: 'Abyss' },
  { hex: '#111827', name: 'Basalt' },
  { hex: '#18181B', name: 'Zinc' },
  { hex: '#1A1A1A', name: 'Noir' },
  { hex: '#09090B', name: 'Obsidian' },
  { hex: '#1E293B', name: 'Slate Dark' },
  { hex: '#171717', name: 'Neutral' },
  { hex: '#0F0F0F', name: 'Onyx' },
  { hex: '#1C1917', name: 'Espresso' },

  { hex: '#FFFFFF', name: 'White' },
  { hex: '#F8FAFC', name: 'Snow' },
  { hex: '#F1F5F9', name: 'Cloud' },
  { hex: '#E2E8F0', name: 'Mist' },
  { hex: '#F0FDF4', name: 'Mint Ice' },
  { hex: '#F0F9FF', name: 'Alice Blue' },
  { hex: '#FAFAFA', name: 'Gallery' },
  { hex: '#F5F5F5', name: 'Smoke' },

  { hex: '#EF4444', name: 'Red Alert' },
  { hex: '#DC2626', name: 'Crimson' },
  { hex: '#B91C1C', name: 'Brick' },
  { hex: '#FF0000', name: 'Hyper Red' },
  { hex: '#F43F5E', name: 'Rose' },
  { hex: '#FB7185', name: 'Coral' },
  { hex: '#F97316', name: 'Tangerine' },
  { hex: '#EA580C', name: 'Blaze' },
  { hex: '#C2410C', name: 'Rust' },
  { hex: '#FACC15', name: 'Cyber Yellow' },
  { hex: '#EAB308', name: 'Gold' },
  { hex: '#CA8A04', name: 'Mustard' },
  { hex: '#84CC16', name: 'Toxic Lime' },
  { hex: '#65A30D', name: 'Alien' },
  { hex: '#4D7C0F', name: 'Olive' },
  { hex: '#22C55E', name: 'Matrix' },
  { hex: '#16A34A', name: 'Emerald' },
  { hex: '#15803D', name: 'Forest' },
  { hex: '#10B981', name: 'Teal' },
  { hex: '#14B8A6', name: 'Turquoise' },
  { hex: '#06B6D4', name: 'Cyan' },
  { hex: '#22D3EE', name: 'Electric Blue' },
  { hex: '#0EA5E9', name: 'Sky' },
  { hex: '#3B82F6', name: 'Royal' },
  { hex: '#2563EB', name: 'Cobalt' },
  { hex: '#1D4ED8', name: 'Deep Sea' },
  { hex: '#6366F1', name: 'Indigo' },
  { hex: '#8B5CF6', name: 'Violet' },
  { hex: '#7C3AED', name: 'Amethyst' },
  { hex: '#6D28D9', name: 'Grape' },
  { hex: '#D946EF', name: 'Fuchsia' },
  { hex: '#C026D3', name: 'Magenta' },
  { hex: '#EC4899', name: 'Hot Pink' },
  { hex: '#41FFA7', name: 'Spring Mint' },
  { hex: '#C4FF4D', name: 'Volt' },
  { hex: '#A5F3FC', name: 'Glacier' }
]

function normalizeHex(hex) {
  if (!hex) return '#000000'
  let h = String(hex).trim()
  if (!h.startsWith('#')) h = '#' + h
  if (h.length === 4) h = '#' + h[1] + h[1] + h[2] + h[2] + h[3] + h[3]
  return h.toUpperCase()
}

const EXACT_NAME_MAP = new Map(NAMED_COLORS.map(c => [normalizeHex(c.hex), c.name]))

const showBackToTop = ref(false)

const onScroll = () => {
  showBackToTop.value = window.scrollY > 400
}

const scrollToTop = () => {
  window.scrollTo({
    top: 0,
    behavior: 'smooth'
  })
}

onMounted(() => {
  window.addEventListener('scroll', onScroll, { passive: true })
})

onUnmounted(() => {
  window.removeEventListener('scroll', onScroll)
})

function hexToRgb(hex) {
  const h = normalizeHex(hex)
  return {
    r: parseInt(h.substring(1, 3), 16),
    g: parseInt(h.substring(3, 5), 16),
    b: parseInt(h.substring(5, 7), 16)
  }
}

function srgbToLinear(u) {
  const v = u / 255
  return v <= 0.04045 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4)
}

function rgbToOklab({ r, g, b }) {
  const R = srgbToLinear(r)
  const G = srgbToLinear(g)
  const B = srgbToLinear(b)

  const l = 0.4122214708 * R + 0.5363325363 * G + 0.0514459929 * B
  const m = 0.2119034982 * R + 0.6806995451 * G + 0.1073969566 * B
  const s = 0.0883024619 * R + 0.2817188376 * G + 0.6299787005 * B

  const l_ = Math.cbrt(l)
  const m_ = Math.cbrt(m)
  const s_ = Math.cbrt(s)

  return {
    L: 0.2104542553 * l_ + 0.7936177850 * m_ - 0.0040720468 * s_,
    a: 1.9779984951 * l_ - 2.4285922050 * m_ + 0.4505937099 * s_,
    b: 0.0259040371 * l_ + 0.7827717662 * m_ - 0.8086757660 * s_
  }
}

function oklabDistance(hex1, hex2) {
  const lab1 = rgbToOklab(hexToRgb(hex1))
  const lab2 = rgbToOklab(hexToRgb(hex2))
  const dL = lab1.L - lab2.L
  const da = lab1.a - lab2.a
  const db = lab1.b - lab2.b
  return Math.sqrt(dL * dL + da * da + db * db)
}

function rgbToHsl({ r, g, b }) {
  const rr = r / 255
  const gg = g / 255
  const bb = b / 255
  const max = Math.max(rr, gg, bb)
  const min = Math.min(rr, gg, bb)
  const d = max - min
  let h = 0
  let s = 0
  const l = (max + min) / 2

  if (d !== 0) {
    s = d / (1 - Math.abs(2 * l - 1))
    switch (max) {
      case rr:
        h = ((gg - bb) / d) % 6
        break
      case gg:
        h = (bb - rr) / d + 2
        break
      default:
        h = (rr - gg) / d + 4
        break
    }
    h = Math.round(h * 60)
    if (h < 0) h += 360
  }
  return { h, s, l }
}

function titleCase(s) {
  return String(s)
    .split(' ')
    .filter(Boolean)
    .map(w => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ')
}

function hueFamily(h) {
  const H = ((h % 360) + 360) % 360
  if (H < 12) return 'Crimson'
  if (H < 26) return 'Scarlet'
  if (H < 45) return 'Amber'
  if (H < 60) return 'Gold'
  if (H < 78) return 'Citrus'
  if (H < 105) return 'Lime'
  if (H < 135) return 'Green'
  if (H < 160) return 'Emerald'
  if (H < 185) return 'Teal'
  if (H < 205) return 'Cyan'
  if (H < 225) return 'Sky'
  if (H < 245) return 'Blue'
  if (H < 265) return 'Indigo'
  if (H < 290) return 'Violet'
  if (H < 315) return 'Purple'
  if (H < 338) return 'Magenta'
  return 'Rose'
}

function neutralFamily({ r, g, b }, l) {
  const spread = Math.max(r, g, b) - Math.min(r, g, b)
  if (spread > 18) return null
  if (l <= 0.09) return 'Pitch'
  if (l <= 0.16) return 'Onyx'
  if (l <= 0.26) return 'Charcoal'
  if (l <= 0.38) return 'Graphite'
  if (l <= 0.52) return 'Slate'
  if (l <= 0.68) return 'Mist'
  if (l <= 0.82) return 'Pearl'
  return 'Snow'
}

function lightnessPrefix(l) {
  if (l <= 0.09) return 'Midnight'
  if (l <= 0.16) return 'Deep'
  if (l <= 0.26) return 'Dark'
  if (l <= 0.38) return 'Dusky'
  if (l <= 0.52) return 'Muted'
  if (l <= 0.68) return 'Soft'
  if (l <= 0.82) return 'Pale'
  return 'Bright'
}

function saturationQualifier(s) {
  if (s <= 0.10) return ''
  if (s <= 0.28) return 'Dust'
  if (s <= 0.50) return 'Tone'
  if (s <= 0.72) return 'Glow'
  return 'Neon'
}

function stableGeneratedName(hex) {
  const rgb = hexToRgb(hex)
  const { h, s, l } = rgbToHsl(rgb)

  const neutral = neutralFamily(rgb, l)
  if (neutral) return neutral

  const fam = hueFamily(h)
  const pre = lightnessPrefix(l)
  const sat = saturationQualifier(s)

  const parts = [pre]
  if (sat) parts.push(sat)
  parts.push(fam)

  return titleCase(parts.join(' '))
}

function getPreciseColorName(inputHex) {
  const hex = normalizeHex(inputHex)
  const exact = EXACT_NAME_MAP.get(hex)
  if (exact) return exact

  let bestName = null
  let bestDist = Infinity

  for (const c of NAMED_COLORS) {
    const candHex = normalizeHex(c.hex)
    const d = oklabDistance(hex, candHex)
    if (d < bestDist) {
      bestDist = d
      bestName = c.name
    }
  }

  const ACCEPT_OKLAB_DISTANCE = 0.045
  if (bestName && bestDist <= ACCEPT_OKLAB_DISTANCE) return bestName

  return stableGeneratedName(hex)
}

function getTextColor(hex, light = '#FFFFFF', dark = '#111111') {
  const { r, g, b } = hexToRgb(hex)
  const luminance = 0.2126 * r + 0.7152 * g + 0.0722 * b
  return luminance > 160 ? dark : light
}

const DuoSplitTemplate = defineComponent({
  name: 'DuoSplitTemplate',
  props: { colors: Array, isExport: Boolean },
  setup(props) {
    return () => {
      const isExport = props.isExport
      const top = props.colors[0] || { name: 'Color A', hex: '#000000' }
      const bottom = props.colors[1] || { name: 'Color B', hex: '#FFFFFF' }

      const labelClass = isExport ? 'text-[22px] uppercase tracking-[0.28em] opacity-90' : 'text-[12px] uppercase tracking-[0.20em] opacity-85'
      const nameClass = isExport ? 'text-6xl sm:text-7xl font-semibold' : 'text-xl font-semibold'
      const hexClass = isExport ? 'text-3xl sm:text-4xl font-mono opacity-85' : 'text-sm font-mono opacity-80'

      return h('div', { class: 'h-full w-full flex flex-col' }, [
        h('div', { class: 'flex-1 flex items-center justify-center', style: { backgroundColor: top.hex } }, [
          h('div', { class: 'text-center space-y-1', style: { color: getTextColor(top.hex) } }, [
            h('p', { class: labelClass }, top.roleLabel || 'Primary'),
            h('p', { class: nameClass }, top.name),
            h('p', { class: hexClass }, top.hex)
          ])
        ]),
        h('div', { class: 'flex-1 flex items-center justify-center', style: { backgroundColor: bottom.hex } }, [
          h('div', { class: 'text-center space-y-1', style: { color: getTextColor(bottom.hex) } }, [
            h('p', { class: labelClass }, bottom.roleLabel || 'Accent'),
            h('p', { class: nameClass }, bottom.name),
            h('p', { class: hexClass }, bottom.hex)
          ])
        ])
      ])
    }
  }
})

const NeonGridTemplate = defineComponent({
  name: 'NeonGridTemplate',
  props: { colors: Array, isExport: Boolean },
  setup(props) {
    return () => {
      const isExport = props.isExport
      const base = props.colors[0]
      const tiles = props.colors.slice(1, 5)

      const headerLabelClass = isExport ? 'text-[14px] uppercase tracking-[0.22em]' : 'text-[10px] uppercase tracking-[0.18em]'
      const headerTitleClass = isExport ? 'text-4xl sm:text-5xl font-semibold mt-2' : 'text-sm font-semibold mt-1'
      const tileRoleClass = isExport ? 'text-sm sm:text-base uppercase tracking-[0.18em] mb-1' : 'text-[10px] uppercase tracking-[0.16em] mb-1'
      const tileTextClass = isExport ? 'text-lg sm:text-xl font-medium text-center' : 'text-xs font-medium text-center'
      const tileHexClass = isExport ? 'text-base sm:text-lg font-mono mt-0.5' : 'text-[11px] font-mono mt-0.5'

      return h('div', { class: 'h-full w-full grid grid-rows-[1.1fr_1.3fr]' }, [
        h('div', { class: 'relative flex items-center px-4', style: { backgroundColor: base.hex } }, [
          h('div', [
            h('p', { class: headerLabelClass, style: { color: getTextColor(base.hex) } }, 'PALETTE'),
            h('p', { class: headerTitleClass, style: { color: getTextColor(base.hex) } }, base.name)
          ])
        ]),
        h('div', { class: 'grid grid-cols-2' }, tiles.map(c =>
          h('div', { class: 'flex flex-col items-center justify-center', style: { backgroundColor: c.hex } }, [
            h('p', { class: tileRoleClass, style: { color: getTextColor(c.hex) } }, c.roleLabel),
            h('div', { class: tileTextClass, style: { color: getTextColor(c.hex) } }, [
              h('p', c.name),
              h('p', { class: tileHexClass }, c.hex)
            ])
          ])
        ))
      ])
    }
  }
})

const TripleStripeTemplate = defineComponent({
  name: 'TripleStripeTemplate',
  props: { colors: Array, isExport: Boolean },
  setup(props) {
    return () => {
      const isExport = props.isExport
      const items = props.colors.slice(0, 3)

      const roleClass = isExport ? 'text-sm sm:text-base uppercase tracking-[0.2em]' : 'text-[10px] uppercase tracking-[0.16em]'
      const labelClass = isExport ? 'text-base sm:text-lg font-bold' : 'text-[11px] font-bold'
      const hexClass = isExport ? 'font-mono text-lg sm:text-xl' : 'font-mono text-xs'
      const aaClass = isExport ? 'text-6xl sm:text-7xl font-semibold' : 'text-2xl font-semibold'

      return h('div', { class: 'h-full w-full flex flex-col' }, [
        items.map(c => {
          const textColor = getTextColor(c.hex)
          return h('div', { class: 'flex-1 flex items-center justify-between px-4', style: { backgroundColor: c.hex } }, [
            h('div', { class: 'space-y-0.5', style: { color: textColor } }, [
              h('p', { class: roleClass }, c.roleLabel),
              h('div', { class: labelClass }, c.name),
              h('div', { class: hexClass }, c.hex)
            ]),
            h('div', { class: aaClass, style: { color: textColor } }, 'Aa')
          ])
        })
      ])
    }
  }
})

const CardStackTemplate = defineComponent({
  name: 'CardStackTemplate',
  props: { colors: Array, isExport: Boolean },
  setup(props) {
    return () => {
      const isExport = props.isExport
      const cards = props.colors.slice(0, 5)
      const accent = cards[2] || cards[0]

      const containerClass = isExport
        ? 'relative w-[92%] max-w-[880px] h-[78%] flex flex-col items-stretch justify-center space-y-3 drop-shadow-[0_30px_60px_rgba(0,0,0,0.75)]'
        : 'relative w-[72%] max-w-xs flex flex-col items-stretch justify-center drop-shadow-[0_20px_40px_rgba(0,0,0,0.7)]'
      const cardBaseClass = isExport
        ? 'flex-1 min-h-[90px] rounded-[32px] border border-black/40 flex items-center justify-between px-8 text-3xl sm:text-4xl font-mono shadow-xl'
        : 'h-8 sm:h-10 rounded-[26px] border border-black/40 flex items-center justify-between px-4 text-[11px] font-mono mb-1.5 shadow-lg'
      const offsets = isExport
        ? ['translate-y-0', '-translate-y-3', '-translate-y-6', '-translate-y-9', '-translate-y-12']
        : ['translate-y-0', '-translate-y-2', '-translate-y-4', '-translate-y-6', '-translate-y-8']

      return h('div', {
        class: 'relative h-full w-full flex items-center justify-center rounded-[40px] overflow-hidden',
        style: {
          backgroundImage: `
            radial-gradient(circle at 50% 80%, ${accent.hex}55 0, transparent 55%),
            linear-gradient(to bottom, #050505 0%, #141414 30%, #101010 70%, #050505 100%)
          `
        }
      }, [
        h('div', { class: containerClass }, cards.map((c, i) =>
          h('div', {
            class: `${cardBaseClass} ${offsets[i]}`,
            style: { backgroundColor: c.hex, zIndex: 10 - i }
          }, [
            h('span', { class: 'font-bold', style: { color: getTextColor(c.hex) } }, c.name),
            h('span', { style: { color: getTextColor(c.hex) } }, c.hex)
          ])
        ))
      ])
    }
  }
})

const QuadrantTemplate = defineComponent({
  name: 'QuadrantTemplate',
  props: { colors: Array, isExport: Boolean },
  setup(props) {
    return () => {
      const isExport = props.isExport
      const blocks = props.colors.slice(0, 4)

      const roleClass = isExport ? 'text-sm sm:text-base uppercase tracking-[0.22em]' : 'text-[10px] uppercase tracking-[0.18em]'
      const nameClass = isExport ? 'text-3xl sm:text-4xl font-semibold' : 'text-sm font-semibold'
      const hexClass = isExport ? 'text-lg sm:text-xl font-mono mt-2' : 'text-[11px] font-mono mt-1'
      const cellClass = isExport ? 'flex flex-col justify-between px-12 py-10 sm:px-16 sm:py-14' : 'flex flex-col justify-between p-4 sm:p-5'

      return h('div', { class: 'h-full w-full grid grid-cols-2 grid-rows-2' },
        blocks.map(c => {
          const textColor = getTextColor(c.hex)
          return h('div', { class: cellClass, style: { backgroundColor: c.hex, color: textColor } }, [
            h('p', { class: roleClass }, c.roleLabel),
            h('div', [
              h('p', { class: nameClass }, c.name),
              h('p', { class: hexClass }, c.hex)
            ])
          ])
        })
      )
    }
  }
})

const GradientFullTemplate = defineComponent({
  name: 'GradientFullTemplate',
  props: { colors: Array, isExport: Boolean },
  setup(props) {
    return () => {
      const isExport = props.isExport
      const hexes = props.colors.map(s => s.hex)
      const gradient = `linear-gradient(135deg, ${hexes.join(', ')})`

      const labelClass = isExport ? 'text-[18px] uppercase tracking-[0.25em]' : 'text-[11px] uppercase tracking-[0.20em]'
      const nameClass = isExport ? 'text-4xl sm:text-5xl font-semibold' : 'text-base font-semibold'
      const hexClass = isExport ? 'text-base sm:text-lg font-mono' : 'text-[11px] font-mono'

      return h('div', { class: 'h-full w-full flex items-center justify-center px-6', style: { backgroundImage: gradient } }, [
        h('div', { class: 'text-center space-y-2 text-white drop-shadow-[0_18px_45px_rgba(15,23,42,0.9)]' }, [
          h('p', { class: labelClass }, 'GRADIENT SYSTEM'),
          h('p', { class: nameClass }, props.colors[0].name + ' Flow'),
          h('p', { class: hexClass }, hexes.join(' · '))
        ])
      ])
    }
  }
})

const GradientGlowTemplate = defineComponent({
  name: 'GradientGlowTemplate',
  props: { colors: Array, isExport: Boolean },
  setup(props) {
    return () => {
      const isExport = props.isExport
      const hexes = props.colors.map(s => s.hex)
      const center = hexes[1] || hexes[0]

      const labelClass = isExport ? 'text-[16px] uppercase tracking-[0.24em]' : 'text-[11px] uppercase tracking-[0.20em]'
      const nameClass = isExport ? 'text-3xl sm:text-4xl font-semibold' : 'text-sm font-semibold'
      const hexClass = isExport ? 'text-base sm:text-lg font-mono' : 'text-[11px] font-mono'

      return h('div', {
        class: 'h-full w-full flex items-center justify-center rounded-[40px] overflow-hidden',
        style: {
          backgroundImage: `
            radial-gradient(circle at 50% 30%, ${center}88 0, transparent 52%),
            radial-gradient(circle at 0% 100%, ${hexes[2] || center}66 0, transparent 55%),
            radial-gradient(circle at 100% 100%, ${hexes[3] || center}66 0, transparent 55%),
            linear-gradient(160deg, #020617 0%, #020617 40%, #020617 100%)
          `
        }
      }, [
        h('div', { class: 'relative z-10 flex flex-col items-center justify-center px-6 py-4 rounded-3xl bg-black/35 backdrop-blur-xl border border-white/15' }, [
          h('p', { class: labelClass + ' text-slate-200' }, 'AURORA PRESET'),
          h('p', { class: nameClass + ' text-white mt-1.5' }, props.colors[0].name),
          h('p', { class: hexClass + ' text-slate-200 mt-1.5' }, hexes.slice(0, 3).join(' · '))
        ])
      ])
    }
  }
})

function roleLabelForTemplate(templateId, index) {
  if (templateId === 'duo') return index === 0 ? 'Primary' : 'Accent'
  if (templateId === 'triple-stripe') return ['Base', 'Highlight', 'Detail'][index] || 'Accent'
  if (templateId === 'quadrant') return ['Primary', 'Action', 'Canvas', 'Secondary'][index] || 'Accent'
  if (templateId === 'card-stack') return ['Base', 'Surface', 'Highlight', 'Shadow', 'Detail'][index] || 'Card'
  if (templateId === 'neon-grid') return ['Background', 'Accent A', 'Accent B', 'Text', 'Detail'][index] || 'Slot'
  return `Stop ${index + 1}`
}

function colorsLimitForTemplate(templateId) {
  if (templateId === 'duo') return 2
  if (templateId === 'triple-stripe') return 3
  if (templateId === 'quadrant') return 4
  if (templateId === 'card-stack') return 5
  if (templateId === 'neon-grid') return 5
  return 4
}

const EXPORT_SIZE = 1080
const exportRef = ref(null)
const currentExportTemplate = ref(null)

const filters = [
  { id: 'all', label: 'All Templates' },
  { id: 'duo', label: 'Pairs' },
  { id: 'grid', label: 'Grids' },
  { id: 'cards', label: 'Cards' },
  { id: 'gradients', label: 'Gradients' }
]

const paletteCombos = [
  { id: 'neon-night', name: 'Neon Night', description: 'Deep indigo base with neon green accents.', colors: ['#060761', '#41FFA7', '#C4FF4D', '#BA8CFF', '#111111'] },
  { id: 'lava-sunset', name: 'Lava Sunset', description: 'Strong reds fading into peach and soft white.', colors: ['#8B3737', '#FF0000', '#FF8A8A', '#FFE3D4', '#FFFFFF'] },
  { id: 'forest-mint', name: 'Forest Mint', description: 'Dark green shadows with bright mint highlights.', colors: ['#020B08', '#12331F', '#1EC46F', '#9BFFD3', '#F5FFF9'] },
  { id: 'soft-neutrals', name: 'Soft Neutrals', description: 'Clean grayscale base with a subtle accent.', colors: ['#111111', '#3A3A3A', '#777777', '#CFCFCF', '#FFFFFF'] },
  { id: 'arctic-dawn', name: 'Arctic Dawn', description: 'Icy blues with a soft pink morning light.', colors: ['#020617', '#0F172A', '#1D4ED8', '#38BDF8', '#F9A8D4'] },
  { id: 'midnight-oasis', name: 'Midnight Oasis', description: 'Deep teal night tones with sand highlights.', colors: ['#020617', '#022C22', '#0F766E', '#22C55E', '#F5F5F4'] },
  { id: 'sunlit-clay', name: 'Sunlit Clay', description: 'Earthy clay oranges under bright daylight.', colors: ['#1C1917', '#7C2D12', '#EA580C', '#FDBA74', '#FEF3C7'] },
  { id: 'cloudy-lilac', name: 'Cloudy Lilac', description: 'Muted lilac grays with soft sky tints.', colors: ['#020617', '#111827', '#4C1D95', '#A855F7', '#E5DEFF'] },
  { id: 'peach-fog', name: 'Peach Fog', description: 'Soft peaches and creams in a morning haze.', colors: ['#0F172A', '#7F1D1D', '#FB7185', '#FED7AA', '#FFF7ED'] },
  { id: 'electric-coral', name: 'Electric Coral', description: 'Bold coral reds with cool ocean contrasts.', colors: ['#020617', '#7F1D1D', '#F97373', '#22C55E', '#E0F2FE'] },
  { id: 'moss-ember', name: 'Moss Ember', description: 'Smoky embers paired with mossy greens.', colors: ['#030712', '#1C1917', '#4D7C0F', '#84CC16', '#F5F5F4'] },
  { id: 'silver-noise', name: 'Silver Noise', description: 'High-contrast grays with a bright highlight.', colors: ['#020617', '#111827', '#4B5563', '#D1D5DB', '#F9FAFB'] },
  { id: 'velvet-grape', name: 'Velvet Grape', description: 'Rich purple tones with warm golden contrast.', colors: ['#020617', '#2E1065', '#7E22CE', '#FACC15', '#FEFCE8'] },
  { id: 'mint-cream', name: 'Mint Cream', description: 'Soft mints and off-whites with subtle depth.', colors: ['#022C22', '#064E3B', '#22C55E', '#BBF7D0', '#F9FAFB'] },
  { id: 'ember-glow', name: 'Ember Glow', description: 'Firey oranges emerging from deep charcoal.', colors: ['#020617', '#111827', '#EA580C', '#FB923C', '#FEF9C3'] },
  { id: 'cosmic-ink', name: 'Cosmic Ink', description: 'Space blues with neon cyan highlights.', colors: ['#020617', '#0B1120', '#1D4ED8', '#22D3EE', '#E0F2FE'] },
  { id: 'sage-stone', name: 'Sage Stone', description: 'Sage greens paired with warm stone neutrals.', colors: ['#0F172A', '#374151', '#4B5563', '#84CC16', '#F3F4F6'] },
  { id: 'retro-cola', name: 'Retro Cola', description: 'Classic cola reds and creams with teal accents.', colors: ['#111827', '#7F1D1D', '#DC2626', '#0369A1', '#FEF3C7'] },
  { id: 'holographic-glass', name: 'Holographic Glass', description: 'Glass-like blues with prismatic pink and lime.', colors: ['#020617', '#0EA5E9', '#22C55E', '#FB7185', '#F9A8FF'] },
  { id: 'crisp-paper', name: 'Crisp Paper', description: 'Ultra-clean grays on a bright white base.', colors: ['#020617', '#111827', '#6B7280', '#E5E7EB', '#FFFFFF'] },
  { id: 'sandstorm', name: 'Sandstorm', description: 'Golden sands under a hazy desert sky.', colors: ['#1C1917', '#854D0E', '#F59E0B', '#FDE68A', '#FEFCE8'] },
  { id: 'polar-mist', name: 'Polar Mist', description: 'Soft arctic blues fading into white fog.', colors: ['#020617', '#0EA5E9', '#38BDF8', '#BAE6FD', '#F9FAFB'] },
  { id: 'midnight-blush', name: 'Midnight Blush', description: 'Dark blue base with romantic blush accents.', colors: ['#020617', '#0F172A', '#1F2937', '#FB7185', '#FCE7F3'] },
  { id: 'amber-forest', name: 'Amber Forest', description: 'Deep greens with glowing amber lights.', colors: ['#022C22', '#166534', '#F59E0B', '#FACC15', '#FEF9C3'] },
  { id: 'petal-smoke', name: 'Petal Smoke', description: 'Smoky grays with muted floral pastels.', colors: ['#020617', '#4B5563', '#E879F9', '#F9A8D4', '#FDF2FF'] },
  { id: 'inkstone', name: 'Inkstone', description: 'Near-black blues with subtle slate contrast.', colors: ['#020617', '#020617', '#111827', '#1F2937', '#CBD5F5'] },
  { id: 'citrus-burst', name: 'Citrus Burst', description: 'Punchy citrus yellows, limes and blood orange.', colors: ['#022C22', '#65A30D', '#A3E635', '#F97316', '#FEF3C7'] },
  { id: 'neon-pulse', name: 'Neon Pulse', description: 'High-energy magenta and lime on deep black.', colors: ['#020617', '#111827', '#22C55E', '#EC4899', '#F9A8FF'] },
  { id: 'dusky-rose', name: 'Dusky Rose', description: 'Dusty roses and browns for a calm mood.', colors: ['#111827', '#4B5563', '#B91C1C', '#FB7185', '#FCE7F3'] },
  { id: 'coffee-cream', name: 'Coffee Cream', description: 'Dark espresso tones with foamy cream.', colors: ['#0F172A', '#3F2A1D', '#854D0E', '#FACC15', '#FFFBEB'] },
  { id: 'aurora-ice', name: 'Aurora Ice', description: 'Aurora greens sliding into icy blues.', colors: ['#020617', '#064E3B', '#22C55E', '#0EA5E9', '#ECFEFF'] },
  { id: 'plum-silk', name: 'Plum Silk', description: 'Soft plum fabric tones with a golden edge.', colors: ['#111827', '#4C1D95', '#7E22CE', '#FACC15', '#FEFCE8'] },
  { id: 'stone-mist', name: 'Stone Mist', description: 'Soft stone neutrals lost in white mist.', colors: ['#020617', '#1F2937', '#6B7280', '#E5E7EB', '#F9FAFB'] },
  { id: 'lagoon-glow', name: 'Lagoon Glow', description: 'Lush teal waters lit by neon cyan.', colors: ['#022C22', '#0F766E', '#14B8A6', '#22D3EE', '#ECFEFF'] },
  { id: 'terracotta-sun', name: 'Terracotta Sun', description: 'Terracotta clay warmed by bright sunlight.', colors: ['#1C1917', '#7C2D12', '#EA580C', '#F97316', '#FED7AA'] },
  { id: 'cotton-skies', name: 'Cotton Skies', description: 'Soft baby blues with cotton candy pink.', colors: ['#0F172A', '#38BDF8', '#BAE6FD', '#F9A8D4', '#FFF1F2'] },
  { id: 'shadow-gold', name: 'Shadow Gold', description: 'Charcoal shadows with refined golden highlights.', colors: ['#020617', '#111827', '#4B5563', '#FACC15', '#FEF9C3'] },
  { id: 'neon-berry', name: 'Neon Berry', description: 'Electric berries with lime and teal flashes.', colors: ['#020617', '#7E22CE', '#EC4899', '#22C55E', '#A5F3FC'] },
  { id: 'winter-branch', name: 'Winter Branch', description: 'Bare dark branches against a pale winter sky.', colors: ['#020617', '#111827', '#4B5563', '#CBD5F5', '#E5E7EB'] },
  { id: 'blush-sand', name: 'Blush Sand', description: 'Blush pinks laid gently over warm sand.', colors: ['#4B5563', '#FB7185', '#FDA4AF', '#FED7AA', '#FFF7ED'] },
  { id: 'oxidized-copper', name: 'Oxidized Copper', description: 'Rusty coppers with teal oxidation highlights.', colors: ['#111827', '#7C2D12', '#B45309', '#0F766E', '#E0F2FE'] },
  { id: 'midnight-lime', name: 'Midnight Lime', description: 'Dark blue canvas with sharp lime strokes.', colors: ['#020617', '#0F172A', '#1D4ED8', '#A3E635', '#F5F5F4'] },
  { id: 'tangerine-dream', name: 'Tangerine Dream', description: 'Juicy oranges with soft cream and sky.', colors: ['#7C2D12', '#EA580C', '#FDBA74', '#FEF3C7', '#E0F2FE'] },
  { id: 'noir-rose', name: 'Noir Rose', description: 'Blackened petals with deep red glow.', colors: ['#020617', '#111827', '#7F1D1D', '#DC2626', '#FECACA'] },
  { id: 'aurora-violet', name: 'Aurora Violet', description: 'Violet auroras streaked with cyan and lime.', colors: ['#020617', '#4C1D95', '#7E22CE', '#22C55E', '#22D3EE'] },
  { id: 'linen-dusk', name: 'Linen Dusk', description: 'Soft dusk grays over linen whites.', colors: ['#020617', '#374151', '#6B7280', '#E5E7EB', '#FFFFFF'] },
  { id: 'polar-citrus', name: 'Polar Citrus', description: 'Bright citrus pops on icy blue foundations.', colors: ['#0EA5E9', '#38BDF8', '#FACC15', '#A3E635', '#FEFCE8'] },
  { id: 'ember-violet', name: 'Ember Violet', description: 'Smoldering purples edged with orange heat.', colors: ['#020617', '#4C1D95', '#7E22CE', '#EA580C', '#FED7AA'] },
  { id: 'olive-smoke', name: 'Olive Smoke', description: 'Smoky neutrals with olive undertones.', colors: ['#020617', '#1F2937', '#4D7C0F', '#84CC16', '#E5E7EB'] },
  { id: 'soft-meringue', name: 'Soft Meringue', description: 'Fluffy whites with lemon and blush hints.', colors: ['#FEF3C7', '#FACC15', '#F97316', '#F9A8D4', '#FFFFFF'] },
  { id: 'binary-glow', name: 'Binary Glow', description: 'Almost-black interface with lime and cyan code.', colors: ['#020617', '#020617', '#22C55E', '#22D3EE', '#F9FAFB'] },
  { id: 'granite-wave', name: 'Granite Wave', description: 'Granite neutrals washed by ocean blue.', colors: ['#020617', '#111827', '#4B5563', '#0EA5E9', '#E5E7EB'] },
  { id: 'mulberry-cream', name: 'Mulberry Cream', description: 'Mulberry tones over velvety creams.', colors: ['#111827', '#4C1D95', '#BE185D', '#F9A8D4', '#FEF2F2'] },
  { id: 'marsh-fog', name: 'Marsh Fog', description: 'Muted greens fading into morning mist.', colors: ['#022C22', '#14532D', '#65A30D', '#BBF7D0', '#F9FAFB'] },
  { id: 'cobalt-flash', name: 'Cobalt Flash', description: 'Electric cobalt blues with white sparks.', colors: ['#020617', '#1D4ED8', '#2563EB', '#BAE6FD', '#FFFFFF'] },
  { id: 'solar-crust', name: 'Solar Crust', description: 'Cracked earth baked under intense sun.', colors: ['#1C1917', '#7C2D12', '#EA580C', '#FACC15', '#FEF9C3'] },
  { id: 'teal-powder', name: 'Teal Powder', description: 'Soft teal dust cloud with white air.', colors: ['#082F49', '#0F766E', '#14B8A6', '#A5F3FC', '#F9FAFB'] },
  { id: 'charcoal-blossom', name: 'Charcoal Blossom', description: 'Charcoal ground with blossoming pink petals.', colors: ['#020617', '#111827', '#BE185D', '#FB7185', '#FECDD3'] },
  { id: 'honey-slate', name: 'Honey Slate', description: 'Slate blues drizzled with honey highlights.', colors: ['#020617', '#1E293B', '#0EA5E9', '#F59E0B', '#FEF3C7'] },
  { id: 'nordic-fog', name: 'Nordic Fog', description: 'Misty northern blues with soft neutral ice.', colors: ['#020617', '#0F172A', '#1D4ED8', '#CBD5F5', '#E5E7EB'] },
  { id: 'papaya-shift', name: 'Papaya Shift', description: 'Papaya oranges sliding into creamy whites.', colors: ['#7C2D12', '#F97316', '#FDBA74', '#FEF3C7', '#FFFFFF'] },
  { id: 'violet-ember', name: 'Violet Ember', description: 'Dark violets glowing with ember reds.', colors: ['#020617', '#4C1D95', '#7E22CE', '#DC2626', '#FECACA'] },
  { id: 'bamboo-ink', name: 'Bamboo Ink', description: 'Ink-dark tones with bamboo green highlights.', colors: ['#020617', '#111827', '#166534', '#65A30D', '#F5F5F4'] },
  { id: 'orchid-frost', name: 'Orchid Frost', description: 'Frosty whites dusted with orchid purple.', colors: ['#F9FAFB', '#E5DEFF', '#C4B5FD', '#F9A8D4', '#FEF2F2'] },
  { id: 'signal-noise', name: 'Signal Noise', description: 'Dark UI with magenta and cyan signal lines.', colors: ['#020617', '#020617', '#EC4899', '#22D3EE', '#E5E7EB'] },
  { id: 'harbor-mist', name: 'Harbor Mist', description: 'Harbor blues drifting into cool fog.', colors: ['#020617', '#0F172A', '#1E293B', '#BAE6FD', '#E5E7EB'] },
  { id: 'cocoa-shell', name: 'Cocoa Shell', description: 'Chocolate browns with a crisp shell highlight.', colors: ['#111827', '#3F2A1D', '#7C2D12', '#F59E0B', '#FEF3C7'] },
  { id: 'jade-pulse', name: 'Jade Pulse', description: 'Bright jade greens on a dark backdrop.', colors: ['#020617', '#022C22', '#16A34A', '#22C55E', '#DCFCE7'] },
  { id: 'storm-silk', name: 'Storm Silk', description: 'Stormy grays laid over silky navy.', colors: ['#020617', '#0F172A', '#1F2937', '#6B7280', '#E5E7EB'] },
  { id: 'latte-foam', name: 'Latte Foam', description: 'Coffee latte tones with airy foam whites.', colors: ['#3F2A1D', '#854D0E', '#FBBF24', '#FED7AA', '#FFFBEB'] },
  { id: 'neon-seaweed', name: 'Neon Seaweed', description: 'Black sea depths with neon greens and blues.', colors: ['#020617', '#022C22', '#22C55E', '#22D3EE', '#E0F2FE'] },
  { id: 'pearl-graphite', name: 'Pearl Graphite', description: 'Graphite gray balanced with pearly whites.', colors: ['#020617', '#111827', '#4B5563', '#E5E7EB', '#FFFFFF'] },
  { id: 'ember-rose', name: 'Ember Rose', description: 'Hot ember oranges pushing into rose tones.', colors: ['#7C2D12', '#EA580C', '#F97316', '#FB7185', '#FCE7F3'] },
  { id: 'icefield', name: 'Icefield', description: 'Glacial whites and delicate blue shadows.', colors: ['#020617', '#E0F2FE', '#BAE6FD', '#7DD3FC', '#FFFFFF'] },
  { id: 'oasis-dusk', name: 'Oasis Dusk', description: 'Dusk blues over a warm desert oasis.', colors: ['#020617', '#1E293B', '#0F766E', '#F97316', '#FEF3C7'] },
  { id: 'fuchsia-spark', name: 'Fuchsia Spark', description: 'Sharp fuchsia with bright cyan sparks.', colors: ['#020617', '#BE185D', '#EC4899', '#22D3EE', '#FDF2FF'] },
  { id: 'mellow-olive', name: 'Mellow Olive', description: 'Gentle olives and creams for a muted mood.', colors: ['#1C1917', '#4D7C0F', '#84CC16', '#E5E7EB', '#FEFCE8'] },
  { id: 'night-chrome', name: 'Night Chrome', description: 'Metallic grays in a midnight interface.', colors: ['#020617', '#111827', '#4B5563', '#9CA3AF', '#E5E7EB'] },
  { id: 'poppy-mist', name: 'Poppy Mist', description: 'Soft red poppies blurred in warm mist.', colors: ['#7F1D1D', '#F97373', '#FECACA', '#FDE68A', '#FFF7ED'] },
  { id: 'aqua-flash', name: 'Aqua Flash', description: 'Electric aqua tones racing across dark sea.', colors: ['#020617', '#0F172A', '#0EA5E9', '#22D3EE', '#ECFEFF'] },
  { id: 'obsidian-plum', name: 'Obsidian Plum', description: 'Obsidian blacks with deep plum accents.', colors: ['#020617', '#020617', '#4C1D95', '#7E22CE', '#F3E8FF'] },
  { id: 'caramel-glow', name: 'Caramel Glow', description: 'Caramel browns glowing under soft lights.', colors: ['#3F2A1D', '#7C2D12', '#B45309', '#FBBF24', '#FFFBEB'] },
  { id: 'seafoam-paper', name: 'Seafoam Paper', description: 'Crisp paper whites splashed with seafoam.', colors: ['#F9FAFB', '#E0F2FE', '#BAE6FD', '#A7F3D0', '#ECFEFF'] },
  { id: 'ultra-violet-grid', name: 'Ultra Violet Grid', description: 'Retro-futuristic violet grid with neon cyan.', colors: ['#020617', '#1E1B4B', '#7C3AED', '#22D3EE', '#F9A8FF'] },
  { id: 'foggy-peak', name: 'Foggy Peak', description: 'Mountain grays vanishing into cold fog.', colors: ['#020617', '#111827', '#4B5563', '#CBD5F5', '#E5E7EB'] },
  { id: 'rose-latte', name: 'Rose Latte', description: 'Rose-tinted creams with coffee undertones.', colors: ['#3F2A1D', '#BE185D', '#FB7185', '#FED7AA', '#FFF1F2'] },
  { id: 'hazel-field', name: 'Hazel Field', description: 'Hazel browns and muted greens in a field.', colors: ['#1C1917', '#3F3F46', '#4D7C0F', '#A3E635', '#E7E5E4'] },
  { id: 'polar-berry', name: 'Polar Berry', description: 'Icy blues with a bold berry highlight.', colors: ['#0F172A', '#1D4ED8', '#38BDF8', '#EC4899', '#FDF2FF'] },
  { id: 'night-amber', name: 'Night Amber', description: 'Amber lights floating over deep night tones.', colors: ['#020617', '#111827', '#F59E0B', '#FACC15', '#FEF9C3'] },
  { id: 'misty-lilac', name: 'Misty Lilac', description: 'Lilac haze drifting over deep navy.', colors: ['#020617', '#1E293B', '#4C1D95', '#A855F7', '#EDE9FE'] },
  { id: 'graphite-mint', name: 'Graphite Mint', description: 'Graphite neutrals with crisp mint accents.', colors: ['#020617', '#111827', '#10B981', '#6EE7B7', '#ECFDF5'] },
  { id: 'peach-graph', name: 'Peach Graph', description: 'Toasty browns with glowing peach highlights.', colors: ['#451A03', '#7C2D12', '#F97316', '#FED7AA', '#FFF7ED'] },
  { id: 'azure-cream', name: 'Azure Cream', description: 'Azure blues smoothing into bright cream.', colors: ['#0F172A', '#1D4ED8', '#38BDF8', '#E0F2FE', '#F9FAFB'] },
  { id: 'violet-mist', name: 'Violet Mist', description: 'Violet gradients wrapped in soft mist.', colors: ['#020617', '#312E81', '#7C3AED', '#A5B4FC', '#EEF2FF'] },
  { id: 'shadow-ember', name: 'Shadow Ember', description: 'Smoldering embers emerging from shadow.', colors: ['#020617', '#111827', '#7C2D12', '#EA580C', '#F97316'] },
  { id: 'teal-sand', name: 'Teal Sand', description: 'Teal tides rolling onto warm sand.', colors: ['#022C22', '#0F766E', '#14B8A6', '#FDBA74', '#FEF3C7'] },
  { id: 'cotton-mint', name: 'Cotton Mint', description: 'Cotton candy skies over mint foam.', colors: ['#ECFEFF', '#BAE6FD', '#A7F3D0', '#BBF7D0', '#F9FAFB'] },
  { id: 'crimson-noir', name: 'Crimson Noir', description: 'Crimson strikes cutting through deep noir.', colors: ['#020617', '#111827', '#7F1D1D', '#B91C1C', '#FCA5A5'] },
  { id: 'sky-lime', name: 'Sky Lime', description: 'Sky blues lit by energetic lime.', colors: ['#0EA5E9', '#38BDF8', '#22C55E', '#A3E635', '#F9FAFB'] },
  { id: 'mono-paper', name: 'Mono Paper', description: 'Monochrome stack fading into pure paper white.', colors: ['#020617', '#111827', '#4B5563', '#9CA3AF', '#F9FAFB'] }
]

const gradientCombos = [
  { id: 'neon-horizon', name: 'Neon Horizon', description: 'Night sky base with neon coral and gold highlights.', colors: ['#050816', '#1F3B7B', '#FF4D67', '#FFD166'] },
  { id: 'midnight-cyan', name: 'Midnight Cyan', description: 'Deep navy fading into electric cyan.', colors: ['#020617', '#0F172A', '#0369A1', '#22D3EE'] },
  { id: 'aurora-lime', name: 'Aurora Lime', description: 'Aurora greens and limes over a night canvas.', colors: ['#020617', '#1D3C72', '#22C55E', '#D9F99D'] },
  { id: 'sunset-noir', name: 'Sunset Noir', description: 'Dark red shadows blending into orange and cream.', colors: ['#020617', '#450A0A', '#F97316', '#FEF3C7'] },
  { id: 'polar-lights', name: 'Polar Lights', description: 'Icy cyan and blue with a soft glow.', colors: ['#020617', '#0EA5E9', '#22D3EE', '#E0F2FE'] },
  { id: 'berry-flash', name: 'Berry Flash', description: 'Magenta, fuchsia and warming orange fused together.', colors: ['#020617', '#BE185D', '#EC4899', '#F97316'] },
  { id: 'jade-stream', name: 'Jade Stream', description: 'Teal and jade sliding into mint foam.', colors: ['#022C22', '#0F766E', '#22C55E', '#A7F3D0'] },
  { id: 'violet-ice', name: 'Violet Ice', description: 'Cool violets dissolving into blue ice.', colors: ['#020617', '#4C1D95', '#7C3AED', '#BAE6FD'] },
  { id: 'ember-waves', name: 'Ember Waves', description: 'Ember oranges flowing into gold and cream.', colors: ['#7C2D12', '#EA580C', '#F59E0B', '#FEF3C7'] },
  { id: 'cyan-orbit', name: 'Cyan Orbit', description: 'Dark space ringed by softly glowing cyan.', colors: ['#020617', '#0F172A', '#0EA5E9', '#A5F3FC'] },
  { id: 'violet-dawn', name: 'Violet Dawn', description: 'Violet bands rising into soft pink daylight.', colors: ['#020617', '#4C1D95', '#7C3AED', '#F9A8FF'] },
  { id: 'ember-drift', name: 'Ember Drift', description: 'Ember oranges drifting through deep night.', colors: ['#111827', '#7C2D12', '#EA580C', '#FCD34D'] },
  { id: 'aqua-signal', name: 'Aqua Signal', description: 'Electric aqua beams cutting through dark UI.', colors: ['#020617', '#0EA5E9', '#22D3EE', '#E5E7EB'] },
  { id: 'mint-storm', name: 'Mint Storm', description: 'Stormy greens melting into mint foam.', colors: ['#022C22', '#16A34A', '#22C55E', '#ECFDF5'] },
  { id: 'golden-hour', name: 'Golden Hour', description: 'Indigo twilight tipped with warm gold.', colors: ['#312E81', '#F97316', '#FACC15', '#FEF9C3'] },
  { id: 'orchid-breeze', name: 'Orchid Breeze', description: 'Orchid lavenders swirling into blushing pink.', colors: ['#020617', '#4C1D95', '#E879F9', '#F9A8D4'] },
  { id: 'frostline', name: 'Frostline', description: 'Arctic blues sliding into soft powder snow.', colors: ['#0F172A', '#1D4ED8', '#BAE6FD', '#E0F2FE'] },
  { id: 'carbon-rose', name: 'Carbon Rose', description: 'Carbon blacks flaring into neon rose.', colors: ['#020617', '#111827', '#BE185D', '#F9A8D4'] },
  { id: 'tropical-wash', name: 'Tropical Wash', description: 'Teal seas blending into sunset orange.', colors: ['#022C22', '#0F766E', '#F97316', '#FEF3C7'] },
  { id: 'lilac-cloud', name: 'Lilac Cloud', description: 'Lavender clouds fading into pale sky.', colors: ['#111827', '#4C1D95', '#A855F7', '#EDE9FE'] },
  { id: 'signal-glow', name: 'Signal Glow', description: 'Cyan and magenta pulses across a dark panel.', colors: ['#020617', '#EC4899', '#22D3EE', '#F9A8FF'] },
  { id: 'jade-spark', name: 'Jade Spark', description: 'Jade sparks flickering into light mint.', colors: ['#022C22', '#047857', '#22C55E', '#BBF7D0'] },
  { id: 'copper-haze', name: 'Copper Haze', description: 'Copper reds surrounded by warm haze.', colors: ['#3F2A1D', '#7C2D12', '#B45309', '#F97316'] },
  { id: 'polar-aurora', name: 'Polar Aurora', description: 'Aurora greens dancing over polar blue.', colors: ['#020617', '#0EA5E9', '#22C55E', '#F9FAFB'] },
  { id: 'berry-noise', name: 'Berry Noise', description: 'Berry gradients buzzing with neon energy.', colors: ['#020617', '#7E22CE', '#EC4899', '#FCE7F3'] },
  { id: 'lava-field', name: 'Lava Field', description: 'Molten lava pouring into pale ash.', colors: ['#450A0A', '#B91C1C', '#EA580C', '#FED7AA'] },
  { id: 'night-fog', name: 'Night Fog', description: 'Midnight tones dissolving into gray fog.', colors: ['#020617', '#111827', '#4B5563', '#E5E7EB'] },
  { id: 'skyline-glass', name: 'Skyline Glass', description: 'Blue skyline reflected in glassy cyan.', colors: ['#0F172A', '#1D4ED8', '#38BDF8', '#E0F2FE'] },
  { id: 'emerald-core', name: 'Emerald Core', description: 'Dark emerald edge glowing into fresh lime.', colors: ['#022C22', '#065F46', '#16A34A', '#A7F3D0'] },
  { id: 'hologram-wash', name: 'Hologram Wash', description: 'Holographic cyan beams through soft magenta.', colors: ['#020617', '#0EA5E9', '#22D3EE', '#F9A8FF'] },
  { id: 'dusk-lagoon', name: 'Dusk Lagoon', description: 'Dusk blues sinking into teal lagoon.', colors: ['#020617', '#1E293B', '#0F766E', '#22D3EE'] },
  { id: 'amber-stream', name: 'Amber Stream', description: 'Amber and orange flowing into soft cream.', colors: ['#7C2D12', '#EA580C', '#F59E0B', '#FEF3C7'] },
  { id: 'rose-shift', name: 'Rose Shift', description: 'Dense rose hues gliding into gentle blush.', colors: ['#111827', '#BE185D', '#FB7185', '#FCE7F3'] },
  { id: 'plasma-lime', name: 'Plasma Lime', description: 'Neon violet erupting into bright lime.', colors: ['#020617', '#4C1D95', '#A3E635', '#ECFEFF'] },
  { id: 'neon-reef', name: 'Neon Reef', description: 'Neon blues and greens floating through black water.', colors: ['#022C22', '#0EA5E9', '#22D3EE', '#A7F3D0'] },
  { id: 'silver-ember', name: 'Silver Ember', description: 'Metallic gray breaking into ember orange.', colors: ['#020617', '#4B5563', '#F97316', '#FCD34D'] },
  { id: 'magnetic-cyan', name: 'Magnetic Cyan', description: 'Magnetic cyan arcs over midnight navy.', colors: ['#020617', '#0F172A', '#22D3EE', '#A5F3FC'] },
  { id: 'ultra-sunset', name: 'Ultra Sunset', description: 'Violet sunset caught in glowing orange.', colors: ['#4C1D95', '#7C3AED', '#F97316', '#FED7AA'] },
  { id: 'glacier-melt', name: 'Glacier Melt', description: 'Glacial blues warming into bright white.', colors: ['#0F172A', '#1D4ED8', '#38BDF8', '#F9FAFB'] },
  { id: 'citrus-sky', name: 'Citrus Sky', description: 'Citrus yellows lifting into pale sky.', colors: ['#022C22', '#84CC16', '#FACC15', '#FDE68A'] },
  { id: 'nightstream', name: 'Nightstream', description: 'Deep navy sliced by glowing aqua stream.', colors: ['#020617', '#1E293B', '#0EA5E9', '#38BDF8'] },
  { id: 'amber-nebula', name: 'Amber Nebula', description: 'Nebula-like amber arcs over deep black.', colors: ['#020617', '#F59E0B', '#F97316', '#FDE68A'] },
  { id: 'rose-fog', name: 'Rose Fog', description: 'Soft rose gradients fading into foggy cream.', colors: ['#7F1D1D', '#F97373', '#FECACA', '#FEF2F2'] },
  { id: 'aqua-ember', name: 'Aqua Ember', description: 'Aqua beams colliding with ember orange.', colors: ['#0EA5E9', '#22D3EE', '#F97316', '#FEF9C3'] },
  { id: 'violet-fields', name: 'Violet Fields', description: 'Rolling violet fields under milky sky.', colors: ['#312E81', '#4C1D95', '#7C3AED', '#C7D2FE'] },
  { id: 'jade-drift', name: 'Jade Drift', description: 'Jade river drifting into mint mist.', colors: ['#022C22', '#0F766E', '#22C55E', '#D1FAE5'] },
  { id: 'lunar-mist', name: 'Lunar Mist', description: 'Lunar grays fading into soft atmosphere.', colors: ['#020617', '#111827', '#6B7280', '#E5E7EB'] },
  { id: 'coral-sheen', name: 'Coral Sheen', description: 'Coral reds glowing through warm haze.', colors: ['#7F1D1D', '#F97373', '#FB7185', '#FED7AA'] },
  { id: 'prism-flow', name: 'Prism Flow', description: 'Prismatic violet and orange bending into pink.', colors: ['#020617', '#4C1D95', '#F97316', '#F9A8FF'] },
  { id: 'polar-violet', name: 'Polar Violet', description: 'Polar blues shifting into bright violet.', colors: ['#020617', '#1D4ED8', '#7C3AED', '#F5F3FF'] }
]

const templateOptions = [
  { id: 'duo', label: 'Duo Split', filter: 'duo', type: 'duo', statusLabel: 'Square', statusColor: 'bg-emerald-400', exportSize: '1080×1080', component: DuoSplitTemplate },
  { id: 'neon-grid', label: 'Neon Noir Grid', filter: 'grid', type: 'grid', statusLabel: 'Square', statusColor: 'bg-sky-400', exportSize: '1080×1080', component: NeonGridTemplate },
  { id: 'triple-stripe', label: 'Triple Stripes', filter: 'duo', type: 'duo', statusLabel: 'Square', statusColor: 'bg-emerald-400', exportSize: '1080×1080', component: TripleStripeTemplate },
  { id: 'card-stack', label: 'Stacked Cards', filter: 'cards', type: 'cards', statusLabel: 'Square', statusColor: 'bg-emerald-400', exportSize: '1080×1080', component: CardStackTemplate },
  { id: 'quadrant', label: 'Primary Quadrants', filter: 'grid', type: 'grid', statusLabel: 'Square', statusColor: 'bg-sky-400', exportSize: '1080×1080', component: QuadrantTemplate }
]

const gradientTemplateOptions = [
  { id: 'gradient-full', label: 'Soft Diagonal', filter: 'gradients', type: 'gradients', statusLabel: 'Square', statusColor: 'bg-fuchsia-400', exportSize: '1080×1080', component: GradientFullTemplate },
  { id: 'gradient-glow', label: 'Aurora Glow', filter: 'gradients', type: 'gradients', statusLabel: 'Square', statusColor: 'bg-fuchsia-400', exportSize: '1080×1080', component: GradientGlowTemplate }
]

function buildTemplates() {
  const combos = paletteCombos.slice(0, 100)
  const items = []

  combos.forEach((combo, index) => {
    const tplOpt = templateOptions[index % templateOptions.length]
    const limit = colorsLimitForTemplate(tplOpt.id)
    let hexList

    if (tplOpt.id === 'duo') {
      const c0 = combo.colors[0]
      const c3 = combo.colors[3]
      hexList = c0 && c3 ? [c0, c3] : combo.colors.slice(0, 2)
    } else {
      hexList = combo.colors.slice(0, limit)
    }

    const previewColors = hexList.map((hex, i) => {
      const norm = normalizeHex(hex)
      return {
        name: getPreciseColorName(norm),
        hex: norm,
        roleLabel: roleLabelForTemplate(tplOpt.id, i)
      }
    })

    items.push({
      id: `${combo.id}-${tplOpt.id}`,
      name: combo.name,
      category: `Ready-made combo • ${tplOpt.label}`,
      type: tplOpt.type,
      filter: tplOpt.filter,
      statusLabel: tplOpt.statusLabel,
      statusColor: tplOpt.statusColor,
      description: combo.description,
      useCases: tplOpt.useCases,
      aspect: `${previewColors.length} colors`,
      exportSize: tplOpt.exportSize,
      component: tplOpt.component,
      previewColors,
      templateId: tplOpt.id
    })
  })

  gradientCombos.forEach((combo, index) => {
    const tplOpt = gradientTemplateOptions[index % gradientTemplateOptions.length]
    const limit = colorsLimitForTemplate(tplOpt.id)
    const hexList = combo.colors.slice(0, limit)

    const previewColors = hexList.map((hex, i) => {
      const norm = normalizeHex(hex)
      return {
        name: getPreciseColorName(norm),
        hex: norm,
        roleLabel: roleLabelForTemplate(tplOpt.id, i)
      }
    })

    items.push({
      id: `${combo.id}-${tplOpt.id}`,
      name: combo.name,
      category: `Gradient • ${tplOpt.label}`,
      type: tplOpt.type,
      filter: tplOpt.filter,
      statusLabel: tplOpt.statusLabel,
      statusColor: tplOpt.statusColor,
      description: combo.description,
      useCases: tplOpt.useCases,
      aspect: `${previewColors.length} stops`,
      exportSize: tplOpt.exportSize,
      component: tplOpt.component,
      previewColors,
      templateId: tplOpt.id
    })
  })

  return items
}

const allTemplates = buildTemplates()
const selectedFilter = ref('all')

const filteredTemplates = computed(() => {
  if (selectedFilter.value === 'all') return allTemplates
  return allTemplates.filter(t => t.filter === selectedFilter.value)
})

function safeSlug(name) {
  return (name || 'palette').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '')
}

async function downloadTemplatePng(tpl) {
  currentExportTemplate.value = tpl
  await nextTick()
  await new Promise(resolve => setTimeout(resolve, 300))

  const node = exportRef.value
  if (!node) return

  try {
    const canvas = await html2canvas(node, {
      backgroundColor: null,
      scale: 2,
      useCORS: true,
      logging: false,
      allowTaint: true
    })

    const dataUrl = canvas.toDataURL('image/png')
    const link = document.createElement('a')
    link.href = dataUrl
    link.download = `PaletteStudio-${safeSlug(tpl.name)}-${tpl.templateId}.png`
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  } catch (error) {
    console.error('Export failed:', error)
    alert('Could not generate image. Please try again.')
  }
}
</script>

<style>
@keyframes gradient-x {

  0%,
  100% {
    background-position: 0% 50%;
  }

  50% {
    background-position: 100% 50%;
  }
}

.animate-gradient-x {
  background-size: 200% 200%;
  animation: gradient-x 10s ease infinite;
}

.no-scrollbar::-webkit-scrollbar {
  display: none;
}

.no-scrollbar {
  -ms-overflow-style: none;
  scrollbar-width: none;
}

.back-to-top {
  position: fixed;
  right: 1.25rem;
  bottom: 1.75rem;
  z-index: 60;
  height: 44px;
  width: 44px;
  border-radius: 9999px;
  border: 1px solid rgba(148, 163, 184, 0.6);
  background: radial-gradient(circle at 0% 0%, #e0f2fe, #0ea5e9);
  color: #0b1120;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 16px 40px rgba(15, 23, 42, 0.35);
  cursor: pointer;
  transition: transform 0.18s ease-out, box-shadow 0.18s ease-out, filter 0.18s ease-out, opacity 0.18s ease-out;
}

.dark .back-to-top {
  background: radial-gradient(circle at 0% 0%, #0f172a, #0ea5e9);
  color: #e5e7eb;
  border-color: rgba(148, 163, 184, 0.7);
}

.back-to-top:hover {
  transform: translateY(-1px) scale(1.03);
  box-shadow: 0 20px 55px rgba(15, 23, 42, 0.45);
  filter: saturate(1.05);
}

.back-to-top:active {
  transform: translateY(0) scale(0.98);
  box-shadow: 0 10px 28px rgba(15, 23, 42, 0.35);
}

@media (max-width: 640px) {
  .back-to-top {
    right: 0.9rem;
    bottom: 1.25rem;
    height: 40px;
    width: 40px;
  }
}
</style>
